var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { forwardAndUseRef, LogicalDomContext, useLogicalDom, useOnClickOutside } from './util';
var TooltipPlacementContext = React.createContext('top');
export var Tooltip = forwardAndUseRef(function (props_, divRef, setDivRef) {
    var reference = props_.reference, mkTooltipContent = props_.mkTooltipContent, preferPlacement = props_.placement, onFirstUpdate = props_.onFirstUpdate, props = __rest(props_
    // We remember the global trend in placement (as `globalPlacement`) so tooltip chains can bounce
    // off the top and continue downwards or vice versa and initialize to that, but then update
    // the trend (as `ourPlacement`).
    , ["reference", "mkTooltipContent", "placement", "onFirstUpdate"]);
    // We remember the global trend in placement (as `globalPlacement`) so tooltip chains can bounce
    // off the top and continue downwards or vice versa and initialize to that, but then update
    // the trend (as `ourPlacement`).
    var globalPlacement = React.useContext(TooltipPlacementContext);
    var placement = preferPlacement ? preferPlacement : globalPlacement;
    var _a = __read(React.useState(placement), 2), ourPlacement = _a[0], setOurPlacement = _a[1];
    // https://popper.js.org/react-popper/v2/faq/#why-i-get-render-loop-whenever-i-put-a-function-inside-the-popper-configuration
    var onFirstUpdate_ = React.useCallback(function (state) {
        if (state.placement)
            setOurPlacement(state.placement);
        if (onFirstUpdate)
            onFirstUpdate(state);
    }, [onFirstUpdate]);
    var _b = __read(React.useState(null), 2), arrowElement = _b[0], setArrowElement = _b[1];
    var _c = usePopper(reference, divRef.current, {
        modifiers: [
            { name: 'arrow', options: { element: arrowElement } },
            { name: 'offset', options: { offset: [0, 8] } },
        ],
        placement: placement,
        onFirstUpdate: onFirstUpdate_
    }), styles = _c.styles, attributes = _c.attributes, update = _c.update;
    var update_ = React.useCallback(function () { return update === null || update === void 0 ? void 0 : update(); }, [update]);
    var logicalDom = React.useContext(LogicalDomContext);
    var popper = React.createElement("div", __assign({ ref: function (node) {
            setDivRef(node);
            logicalDom.registerDescendant(node);
        }, style: styles.popper, className: 'tooltip' }, props, attributes.popper),
        React.createElement(TooltipPlacementContext.Provider, { value: ourPlacement }, mkTooltipContent(update_)),
        React.createElement("div", { ref: setArrowElement, style: styles.arrow, className: 'tooltip-arrow' }));
    // Append the tooltip to the end of document body to avoid layout issues.
    // (https://github.com/leanprover/vscode-lean4/issues/51)
    return ReactDOM.createPortal(popper, document.body);
});
/** An element which calls `setHoverState` when the hover state of its DOM children changes.
 *
 * It is implemented with JS rather than CSS in order to allow nesting of these elements. When nested,
 * only the smallest (deepest in the DOM tree) {@link DetectHoverSpan} has an enabled hover state. */
export var DetectHoverSpan = forwardAndUseRef(function (props_, ref, setRef) {
    var setHoverState = props_.setHoverState, props = __rest(props_, ["setHoverState"]);
    var onPointerEvent = function (b, e) {
        // It's more composable to let pointer events bubble up rather than to call `stopPropagation`,
        // but we only want to handle hovers in the innermost component. So we record that the
        // event was handled with a property.
        // The `contains` check ensures that the node hovered over is a child in the DOM
        // tree and not just a logical React child (see useLogicalDom and
        // https://reactjs.org/docs/portals.html#event-bubbling-through-portals).
        if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) {
            if ('_DetectHoverSpanSeen' in e)
                return;
            e._DetectHoverSpanSeen = {};
            if (!b)
                setHoverState('off');
            else if (e.ctrlKey || e.metaKey)
                setHoverState('ctrlOver');
            else
                setHoverState('over');
        }
    };
    React.useEffect(function () {
        var onKeyDown = function (e) {
            if (e.key === 'Control' || e.key === 'Meta')
                setHoverState(function (st) { return st === 'over' ? 'ctrlOver' : st; });
        };
        var onKeyUp = function (e) {
            if (e.key === 'Control' || e.key === 'Meta')
                setHoverState(function (st) { return st === 'ctrlOver' ? 'over' : st; });
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('keyup', onKeyUp);
        return function () {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('keyup', onKeyUp);
        };
    }, []);
    return React.createElement("span", __assign({}, props, { ref: setRef, onPointerOver: function (e) {
            onPointerEvent(true, e);
            if (props.onPointerOver)
                props.onPointerOver(e);
        }, onPointerOut: function (e) {
            onPointerEvent(false, e);
            if (props.onPointerOut)
                props.onPointerOut(e);
        }, onPointerMove: function (e) {
            if (e.ctrlKey || e.metaKey)
                setHoverState(function (st) { return st === 'over' ? 'ctrlOver' : st; });
            else
                setHoverState(function (st) { return st === 'ctrlOver' ? 'over' : st; });
            if (props.onPointerMove)
                props.onPointerMove(e);
        } }), props.children);
});
var TipChainContext = React.createContext({ pinParent: function () { } });
/** Shows a tooltip when the children are hovered over or clicked.
 *
 * An `onClick` middleware can optionally be given in order to control what happens when the
 * hoverable area is clicked. The middleware can invoke `next` to execute the default action
 * which is to pin the tooltip open. */
export var WithTooltipOnHover = forwardAndUseRef(function (props_, ref, setRef) {
    var mkTooltipContent = props_.mkTooltipContent, props = __rest(props_
    // We are pinned when clicked, shown when hovered over, and otherwise hidden.
    , ["mkTooltipContent"]);
    var _a = __read(React.useState('hide'), 2), state = _a[0], setState = _a[1];
    var shouldShow = state !== 'hide';
    var tipChainCtx = React.useContext(TipChainContext);
    React.useEffect(function () {
        if (state === 'pin')
            tipChainCtx.pinParent();
    }, [state, tipChainCtx]);
    var newTipChainCtx = React.useMemo(function () { return ({
        pinParent: function () {
            setState('pin');
            tipChainCtx.pinParent();
        }
    }); }, [tipChainCtx]);
    // Note: because tooltips are attached to `document.body`, they are not descendants of the
    // hoverable area in the DOM tree, and the `contains` check fails for elements within tooltip
    // contents. We can use this to distinguish these elements.
    var isWithinHoverable = function (el) { return ref.current && el instanceof Node && ref.current.contains(el); };
    var _b = __read(useLogicalDom(ref), 2), logicalElt = _b[0], logicalDomStorage = _b[1];
    // We use timeouts for debouncing hover events.
    var timeout = React.useRef();
    var clearTimeout = function () {
        if (timeout.current) {
            window.clearTimeout(timeout.current);
            timeout.current = undefined;
        }
    };
    var showDelay = 500;
    var hideDelay = 300;
    var isModifierHeld = function (e) { return (e.altKey || e.ctrlKey || e.shiftKey || e.metaKey); };
    var onClick = function (e) {
        clearTimeout();
        setState(function (state) { return state === 'pin' ? 'hide' : 'pin'; });
    };
    var onClickOutside = React.useCallback(function () {
        clearTimeout();
        setState('hide');
    }, []);
    useOnClickOutside(logicalElt, onClickOutside);
    var isPointerOverTooltip = React.useRef(false);
    var startShowTimeout = function () {
        clearTimeout();
        timeout.current = window.setTimeout(function () {
            setState(function (state) { return state === 'hide' ? 'show' : state; });
            timeout.current = undefined;
        }, showDelay);
    };
    var startHideTimeout = function () {
        clearTimeout();
        timeout.current = window.setTimeout(function () {
            if (!isPointerOverTooltip.current)
                setState(function (state) { return state === 'show' ? 'hide' : state; });
            timeout.current = undefined;
        }, hideDelay);
    };
    var onPointerEnter = function (e) {
        isPointerOverTooltip.current = true;
        clearTimeout();
    };
    var onPointerLeave = function (e) {
        isPointerOverTooltip.current = false;
        startHideTimeout();
    };
    var onPointerEvent = function (act, e) {
        if ('_WithTooltipOnHoverSeen' in e)
            return;
        if (!isWithinHoverable(e.target))
            return;
        e._WithTooltipOnHoverSeen = {};
        act();
    };
    return React.createElement(LogicalDomContext.Provider, { value: logicalDomStorage },
        React.createElement("span", __assign({}, props, { ref: setRef, onClick: function (e) {
                if (!isWithinHoverable(e.target))
                    return;
                e.stopPropagation();
                if (props.onClick !== undefined)
                    props.onClick(e, onClick);
                else
                    onClick(e);
            }, onPointerDown: function (e) {
                // We have special handling for some modifier+click events, so prevent default browser
                // events from interfering when a modifier is held.
                if (isModifierHeld(e))
                    e.preventDefault();
            }, onPointerOver: function (e) {
                if (!isModifierHeld(e))
                    onPointerEvent(startShowTimeout, e);
                if (props.onPointerOver !== undefined)
                    props.onPointerOver(e);
            }, onPointerOut: function (e) {
                onPointerEvent(startHideTimeout, e);
                if (props.onPointerOut !== undefined)
                    props.onPointerOut(e);
            } }),
            shouldShow &&
                React.createElement(TipChainContext.Provider, { value: newTipChainCtx },
                    React.createElement(Tooltip, { reference: ref.current, onPointerEnter: onPointerEnter, onPointerLeave: onPointerLeave, mkTooltipContent: mkTooltipContent })),
            props.children));
});
